import { Component, OnDestroy, OnInit } from "@angular/core";
import { Store } from "@ngrx/store";
import { DynamicDialogRef, DynamicDialogConfig } from "primeng/dynamicdialog";
import { ExportRequestTagsConfiguration } from "./export-request-tags-configuration";
import { map, Observable, Subscription } from "rxjs";
import {
  selectStationTagsByParent,
  selectStationTagsForm,
  FeatureState,
  StationsTagsState,
  selectFeatureStateForm,
  StationTagsActions
} from "../../../../../../store";
import { CommonModule } from "@angular/common";
import { TranslateModule } from "@ngx-translate/core";
import { TableModule } from "primeng/table";
import { TagModule } from "primeng/tag";
import {
  ButtonComponent,
  TableComponent as LibTableComponent,
  PaginationParam,
  TableColumn,
  TableSummary,
  DEFAULT_PAGE,
} from "daisy-design-system-front";
import _ from "lodash";
import { OntologyFormType, MappedTagInfo } from "@gnv-shared/types";
import { FormGroup } from "@angular/forms";
import { DEFAULT_PAGE_SIZE, DEFAULT_OFFSET } from "@gnv-shared/constants";

@Component({
  selector: "app-dialog-export-request-tags",
  templateUrl: "./dialog-export-request-tags.component.html",
  styleUrls: ["./dialog-export-request-tags.component.scss"],
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    TableModule,
    ButtonComponent,
    TagModule,
    LibTableComponent,
  ],
})
export class DialogExportRequestTagsComponent implements OnInit, OnDestroy {
  public toggleFeatureSelect$: Observable<FeatureState>;
  public tableView$: Observable<StationsTagsState>;
  public tableSummary!: TableSummary;
  public tableColumns!: TableColumn[];
  public pagination!: PaginationParam;
  public allSelectedTags: string[] = [];

  public groupChecked!: (parentName: string) => boolean;

  private tableViewSub$!: Subscription;
  private groupParentChangeSub$!: Subscription;

  constructor(
    private readonly store: Store,
    private readonly ref: DynamicDialogRef,
    private readonly tagsConfiguration: ExportRequestTagsConfiguration,
    public config: DynamicDialogConfig<{
      stationUid: string,
      stationName: string,
      valueFormGroup: FormGroup<OntologyFormType>,
    }>,
  ) {

    this.toggleFeatureSelect$ = this.store.select(selectFeatureStateForm);
    this.tableColumns = this.tagsConfiguration.getTableColumns();
    this.allSelectedTags = this.config.data?.valueFormGroup.controls.tags.value || [];

    this.pagination = {
          currentPage: DEFAULT_PAGE,
          limit: DEFAULT_PAGE_SIZE,
          totalItems: 0,
          offset: DEFAULT_OFFSET,
        };

    this.store.dispatch(
      StationTagsActions.search({
        stationUid: this.config.data?.stationUid || "",
      })
    );

    this.tableView$ = this.store
      .select(selectStationTagsForm)
      .pipe(map((state) => _.cloneDeep(state)));

      // TODO
      this.groupChecked = (parent: string) => {
        return this.tableSummary.rows
        .filter(tag => tag['parent'] === parent)
        .every(tag => tag['checked']);
      }
  }

  ngOnInit(): void {
    this.tableViewSub$ = this.tableView$.subscribe((response) => {
      if (response.loading === false && response.hasError === false) {
        this.tableSummary = response.data;

        this.tableSummary.rows.forEach(row => {
          row['checked'] = this.allSelectedTags.includes(row['uid']);
        });
      } else {
        this.tableSummary = { rows: [] };
      }
    });
  }

  onGroupParentChange(event: {checked: boolean, parentName: string}) {
    // Getting all children from the parent
    this.groupParentChangeSub$ = this.store.select(selectStationTagsByParent(event.parentName))
      .subscribe(tags => {
        const childrenIds = tags.map(tag => tag["uid"]);
        const currentTags = this.config.data?.valueFormGroup.controls.tags.value || [];

        if (event.checked) {
          this.allSelectedTags = _.uniq([...currentTags, ...childrenIds]);

        } else {
          this.allSelectedTags = currentTags.filter(item => !childrenIds.includes(item));
        }

        this.config.data?.valueFormGroup.controls.tags.setValue(this.allSelectedTags);

        this.tableSummary.rows.forEach(row => {
          row["checked"] = this.allSelectedTags.includes(row["uid"]);
        });

      })
  }

  onGroupItemChange(event: {checked: boolean, row: MappedTagInfo}) {
    if (event.checked) {
      this.config.data?.valueFormGroup.controls.tags.setValue([...this.config.data.valueFormGroup.controls.tags.value || [], event.row.uid as string]);
    } else {
      const oldTags = this.config.data?.valueFormGroup.controls.tags.value || [];
      this.config.data?.valueFormGroup.controls.tags.setValue(oldTags.filter(item =>  item !== event.row.uid));
    }
  }

  close() {
    this.ref.close();
  }

  ngOnDestroy(): void {
    this.tableViewSub$?.unsubscribe();
    this.groupParentChangeSub$?.unsubscribe();
  }
}
